import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './FollowList.scss';
import { useNavigate } from 'react-router-dom';
import {
  HARD_TTL,
  cacheGetStale,
  cacheSet,
  cacheUpdate,
} from '../../utils/swrcache';
import searchIcon from './img/search.png';
import { getAccessToken } from '../../awsAuth';

const DEFAULT_AVATAR =
  'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

const ANIM_OPEN_MS = 240;
const ANIM_CLOSE_MS = 220;

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.location.hostname.includes('onrender.com')
    ? 'https://cvanto-api.onrender.com'
    : 'http://localhost:5000');

const API = process.env.REACT_APP_API || API_BASE;

const apiUrl = (path) => {
  if (!path) return API;
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(API || '').replace(/\/+$/, '');
  const p = String(path || '');
  return p.startsWith('/') ? `${base}${p}` : `${base}/${p}`;
};

const FOLLOW_CACHE_TTL = 24 * 60 * 60 * 1000; // 24h

const getFollowRevKey = (userId) =>
  userId ? `cv:followRev:${String(userId).trim()}` : null;

const readFollowRev = (userId) => {
  try {
    if (typeof window === 'undefined') return 0;
    const k = getFollowRevKey(userId);
    if (!k) return 0;
    const n = Number(window.localStorage.getItem(k) || 0);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

const bumpFollowRev = (userId) => {
  try {
    if (typeof window === 'undefined') return;
    const k = getFollowRevKey(userId);
    if (!k) return;
    const cur = readFollowRev(userId);
    const next = cur + 1;
    window.localStorage.setItem(k, String(next));
    window.dispatchEvent(
      new CustomEvent('cvanto:followRev', { detail: { userId, rev: next } })
    );
  } catch {}
};

const unwrapCachedUsers = (cached, expectedRev) => {
  if (!cached) return null;
  if (Array.isArray(cached)) {
    return null;
  }
  if (cached && typeof cached === 'object') {
    const rev = Number(cached.rev || 0);
    if (!Number.isFinite(rev)) return null;
    if (rev !== expectedRev) return null;
    const users = Array.isArray(cached.users) ? cached.users : null;
    return users;
  }
  return null;
};

const wrapCachePayload = (users, rev) => ({
  rev: Number.isFinite(Number(rev)) ? Number(rev) : 0,
  users: Array.isArray(users) ? users : [],
});

const FollowList = ({ userId, type, onClose, currentUserId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const cacheKey = `follow:${type}:${userId}`;

  const lastSeenRevRef = useRef(readFollowRev(userId));

  // Scroll lock (modal)
  const scrollYRef = useRef(0);
  useEffect(() => {
    setMounted(true);
    scrollYRef.current = window.scrollY || 0;
    const body = document.body;
    const html = document.documentElement;
    body.style.position = 'fixed';
    body.style.top = `-${scrollYRef.current}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overscrollBehavior = '';
      window.scrollTo(0, scrollYRef.current);
    };
  }, []);

  // Close on Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleRequestClose();
    };
    window.addEventListener('keydown', onKey, { passive: true });
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Load
  useEffect(() => {
    if (!userId || !type) return;
    let cancelled = false;

    const expectedRev = readFollowRev(userId);
    lastSeenRevRef.current = expectedRev;

    const staleRaw = cacheGetStale(cacheKey, FOLLOW_CACHE_TTL);
    const staleUsers = unwrapCachedUsers(staleRaw, expectedRev);

    if (staleUsers && !cancelled) {
      setUsers(staleUsers);
      setLoading(false);
    }

    (async () => {
      try {
        if (!staleUsers) setLoading(true);

        const listRes = await fetch(
          apiUrl(`/api/follow/${encodeURIComponent(type)}/${encodeURIComponent(userId)}`)
        );

        if (!listRes.ok) {
          const text = await listRes.text().catch(() => '');
          throw new Error(`HTTP ${listRes.status} ${text || ''}`.trim());
        }

        const data = await listRes.json();
        const ids =
          type === 'followers'
            ? data.followerIds || []
            : data.followingIds || [];

        const validProfiles = [];
        const seen = new Set();

        for (const id of ids) {
          if (seen.has(id)) continue;
          seen.add(id);
          try {
            const ures = await fetch(
              apiUrl(`/api/account/${encodeURIComponent(id)}`)
            );
            if (!ures.ok) throw new Error('Not found');
            const u = await ures.json();
            validProfiles.push({
              id,
              username: u.username,
              avatar: u.avatar,
            });
          } catch {
            try {
              const followerId = type === 'followers' ? id : userId;
              const followedId = type === 'followers' ? userId : id;
              await fetch(
                apiUrl(`/api/follow/${encodeURIComponent(followerId)}/${encodeURIComponent(followedId)}`),
                { method: 'DELETE' }
              );
            } catch {
            }
          }
        }

        if (!cancelled) {
          setUsers(validProfiles);

          const curRev = readFollowRev(userId);
          cacheSet(cacheKey, wrapCachePayload(validProfiles, curRev));

          setLoading(false);
        }
      } catch (err) {
        console.error('Помилка завантаження списку:', err);
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, type, cacheKey]);

  useEffect(() => {
    if (!userId) return;

    const hardRefresh = async () => {
      try {
        setLoading(true);

        const listRes = await fetch(
          apiUrl(`/api/follow/${encodeURIComponent(type)}/${encodeURIComponent(userId)}`)
        );
        if (!listRes.ok) throw new Error('follow list fetch failed');

        const data = await listRes.json();
        const ids =
          type === 'followers'
            ? data.followerIds || []
            : data.followingIds || [];

        const validProfiles = [];
        const seen = new Set();

        for (const id of ids) {
          if (seen.has(id)) continue;
          seen.add(id);
          try {
            const ures = await fetch(apiUrl(`/api/account/${encodeURIComponent(id)}`));
            if (!ures.ok) throw new Error('Not found');
            const u = await ures.json();
            validProfiles.push({ id, username: u.username, avatar: u.avatar });
          } catch {}
        }

        setUsers(validProfiles);

        const curRev = readFollowRev(userId);
        cacheSet(cacheKey, wrapCachePayload(validProfiles, curRev));
      } catch (e) {
        console.error('Помилка smart refresh follow:', e);
      } finally {
        setLoading(false);
      }
    };

    const checkRevAndRefresh = () => {
      const cur = readFollowRev(userId);
      if (cur !== lastSeenRevRef.current) {
        lastSeenRevRef.current = cur;
        hardRefresh();
      }
    };

    const onStorage = (e) => {
      const k = getFollowRevKey(userId);
      if (e?.key && k && e.key === k) checkRevAndRefresh();
    };

    const onCustom = (e) => {
      if (e?.detail?.userId && String(e.detail.userId) !== String(userId)) return;
      checkRevAndRefresh();
    };

    const onFocus = () => checkRevAndRefresh();
    const onVis = () => {
      if (document.visibilityState === 'visible') checkRevAndRefresh();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('cvanto:followRev', onCustom);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('cvanto:followRev', onCustom);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [userId, type, cacheKey]);

  // Remove follower (owner-only)
  const handleRemoveFollower = async (followerId) => {
    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(apiUrl(`/api/follow/remove-follower`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, followerId, currentUserId }),
      });

      if (!res.ok) throw new Error('Не вдалося видалити підписника');

      setUsers((prev) => prev.filter((user) => user.id !== followerId));

      bumpFollowRev(userId);

      cacheUpdate(cacheKey, (cur) => {
        const expectedRev = readFollowRev(userId);

        if (Array.isArray(cur)) {
          const nextUsers = cur.filter((u) => u?.id !== followerId);
          return wrapCachePayload(nextUsers, expectedRev);
        }
        if (cur && typeof cur === 'object') {
          const list = Array.isArray(cur.users) ? cur.users : [];
          const nextUsers = list.filter((u) => u?.id !== followerId);
          return wrapCachePayload(nextUsers, expectedRev);
        }
        return wrapCachePayload([], expectedRev);
      });
    } catch (err) {
      console.error('Помилка видалення підписника:', err);
    }
  };

  // Local filter
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.username || '').toLowerCase().includes(q));
  }, [users, query]);

  // Close with animation
  const handleRequestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
    }, ANIM_CLOSE_MS);
  }, [isClosing, onClose]);

  return (
    <div
      className="follow-modal"
      data-mounted={mounted ? '1' : '0'}
      data-state={isClosing ? 'closing' : 'open'}
      onClick={handleRequestClose}
      role="dialog"
      aria-modal="true"
      aria-label={type === 'followers' ? 'Підписники' : 'Підписки'}
    >
      <div
        className="follow-modal__content"
        onClick={(e) => e.stopPropagation()}
        data-mounted={mounted ? '1' : '0'}
        data-state={isClosing ? 'closing' : 'open'}
      >
        <header className="follow-modal__header">
          <div className="fm-bar">
            <h3 className="fm-title">
              {type === 'followers' ? 'Підписники' : 'Підписки'}
            </h3>
            <button
              className="fm-close"
              type="button"
              aria-label="Закрити"
              onClick={handleRequestClose}
            >
              <span aria-hidden>×</span>
            </button>
          </div>

          <div className="follow-search">
            <img
              className="search-icon"
              src={searchIcon}
              alt=""
              aria-hidden="true"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Пошук"
              inputMode="search"
              aria-label="Пошук користувачів у списку"
            />
          </div>
        </header>

        <div className="follow-modal__body">
          {loading ? (
            <ul className="follow-skeleton">
              {Array.from({ length: 8 }).map((_, i) => (
                <li
                  key={i}
                  className="sk-row"
                  style={{ animationDelay: `${0.02 * i + 0.04}s` }}
                >
                  <span className="sk-avatar" />
                  <span className="sk-line" />
                </li>
              ))}
            </ul>
          ) : filtered.length === 0 ? (
            <div className="follow-empty">
              <p>Нічого не знайдено.</p>
            </div>
          ) : (
            <ul className="follow-list">
              {filtered.map((user, idx) => (
                <li
                  key={user.id}
                  className="follow-item"
                  style={{ animationDelay: `${0.015 * idx + 0.06}s` }}
                >
                  <button
                    className="user-info"
                    onClick={() => navigate(`/profile/${user.id}`)}
                  >
                    <img src={user.avatar || DEFAULT_AVATAR} alt="" />
                    <span className="username">
                      {user.username || 'Користувач'}
                    </span>
                  </button>

                  {type === 'followers' && currentUserId === userId && (
                    <button
                      className="remove-follower-btn"
                      onClick={() => handleRemoveFollower(user.id)}
                      title="Видалити підписника"
                    >
                      Видалити
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowList;

export const bumpFollowRevision = (userId) => bumpFollowRev(userId);
